/* eslint-disable no-throw-literal */
import gql from 'graphql-tag';
import apolloClient from '../../../../utils/apolloClient';

// ─── GraphQL Query ────────────────────────────────────────────────────────────
//
// search_company_invoice(input: SearchInput): CompanyInvoiceListing
//
// Pagination fields (from, size, sort) sit at the top level of SearchInput.
// All filtering goes into filter.company_invoice_filter (CompanyInvoiceFilter).
// Date range for filtering invoices lives inside company_invoice_filter, not
// at the top level of SearchInput (those top-level from_date/to_date are used
// by company_invoice_summary, not this query).

const SEARCH_COMPANY_INVOICE = gql`
  query SearchCompanyInvoice($input: SearchInput) {
    search_company_invoice(input: $input) {
      total_pages
      total_size
      company_invoice_listing {
        company_invoice_id
        invoice_number
        company_id
        period {
          from
          to
        }
        payment_date
        order_count
        total_places
        sale_value
        kravein_fee
        card_fees
        net_payout
        sub_total_gst
        pdf_url
        csv_url
        admin_pdf_url
        admin_csv_url
        is_cash_include
        status
        audit {
          created_at
          created_by
          updated_at
          updated_by
        }
        company {
          name
          abn
          address_line_1
          city
          state
          post_code
        }
        places {
          place_id
          name
          city
          state
          status
          address_line_1
        }
      }
      error {
        code
        description
      }
    }
  }
`;

// ─── Runtime Validators ───────────────────────────────────────────────────────

const INVOICE_STATUSES = new Set([
  'GENERATED',
  'SENT',
  'PAID',
  'DRAFT',
  'OVERDUE',
  'EMAILSENT',
]);

const assertInvoiceStatus = (val, invoiceId) => {
  if (!val || !INVOICE_STATUSES.has(val)) {
    console.warn(
      `[searchCompanyInvoiceService] Unexpected status: "${val}" for "${invoiceId}", defaulting to -`,
    );
    return '-';
  }
  return val;
};

// ─── Pure Transformers ────────────────────────────────────────────────────────

const toPlace = raw => ({
  placeId: raw?.place_id ?? '',
  name: raw?.name ?? '',
  city: raw?.city ?? '',
  state: raw?.state ?? '',
  status: raw?.status ?? '',
  addressLine1: raw?.address_line_1 ?? '',
});

const toCompany = raw => ({
  name: raw?.name ?? '',
  abn: raw?.abn ?? '',
  addressLine1: raw?.address_line_1 ?? '',
  city: raw?.city ?? '',
  state: raw?.state ?? '',
  postCode: raw?.post_code ?? '',
});

const toPeriod = raw => {
  const toRaw = raw?.to ?? '';
  let to = toRaw;
  if (toRaw) {
    const d = new Date(toRaw);
    d.setUTCDate(d.getUTCDate() - 1);
    to = d.toISOString().replace(/\.\d{3}Z$/, '.000Z');
  }
  return {
    from: raw?.from ?? '',
    to,
  };
};

const toAudit = raw => ({
  createdAt: raw?.created_at ?? '',
  createdBy: raw?.created_by ?? '',
  updatedAt: raw?.updated_at ?? '',
  updatedBy: raw?.updated_by ?? '',
});

const toCompanyInvoice = raw => {
  const invoiceId = raw?.company_invoice_id ?? 'unknown';
  return {
    companyInvoiceId: invoiceId,
    invoiceNumber: raw?.invoice_number ?? '',
    companyId: raw?.company_id ?? '',
    period: toPeriod(raw?.period),
    paymentDate: raw?.payment_date ?? null,
    orderCount: raw?.order_count ?? 0,
    totalPlaces: raw?.total_places ?? 0,
    saleValue: raw?.sale_value ?? 0,
    kraveinFee: raw?.kravein_fee ?? 0,
    cardFees: raw?.card_fees ?? 0,
    netPayout: raw?.net_payout ?? 0,
    subTotalGst: raw?.sub_total_gst ?? 0,
    pdfS3Key: raw?.pdf_s3_key ?? null,
    pdfUrl: raw?.pdf_url ?? null,
    adminPdfUrl: raw?.admin_pdf_url ?? null,
    csvS3Key: raw?.csv_s3_key ?? null,
    csvUrl: raw?.csv_url ?? null,
    adminCsvUrl: raw?.admin_csv_url ?? null,
    isCashInclude: raw?.is_cash_include ?? false,
    status: assertInvoiceStatus(raw?.status, invoiceId),
    audit: toAudit(raw?.audit),
    company: toCompany(raw?.company),
    places: Array.isArray(raw?.places) ? raw.places.map(toPlace) : [],
  };
};

const toInvoicePage = raw => ({
  invoices: Array.isArray(raw?.company_invoice_listing)
    ? raw.company_invoice_listing
        .filter(item => item !== null)
        .map(toCompanyInvoice)
    : [],
  totalPages: raw?.total_pages ?? 0,
  totalSize: raw?.total_size ?? 0,
});

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === 'object' && err !== null && 'code' in err && 'message' in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes('unauthorized') || msg.includes('unauthenticated')) {
      return { message: err.message, code: 'UNAUTHORIZED' };
    }
    if (msg.includes('network') || msg.includes('failed to fetch')) {
      return { message: err.message, code: 'NETWORK_ERROR' };
    }
    return { message: err.message, code: 'UNKNOWN_ERROR' };
  }
  return { message: 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function searchCompanyInvoices(
  params,
  client = apolloClient.clientPrivate,
) {
  const {
    userId,
    from = 0,
    size = 15,
    sort = 'CREATED_DESC',
    filter = {},
  } = params;

  const invoiceFilter = {};
  const placeFilter = {};
  if (filter.companyInvoiceId)
    invoiceFilter.company_invoice_id = filter.companyInvoiceId;
  if (filter.companyId) invoiceFilter.company_id = filter.companyId;
  if (filter.invoiceNumber) invoiceFilter.invoice_number = filter.invoiceNumber;
  if (filter.status) invoiceFilter.status = filter.status;
  if (filter.placeId) placeFilter.place_id = filter.placeId;
  if (filter.fromDate) invoiceFilter.from_date = filter.fromDate;
  if (filter.toDate) invoiceFilter.to_date = filter.toDate;

  try {
    const { data } = await client.query({
      query: SEARCH_COMPANY_INVOICE,
      variables: {
        input: {
          user_id: userId,
          from,
          size,
          sort,
          ...(Object.keys(invoiceFilter).length > 0
            ? {
                filter: {
                  company_invoice_filter: invoiceFilter,
                  place_filter: placeFilter,
                },
              }
            : {}),
        },
      },
      fetchPolicy: 'network-only',
    });

    const result = data?.search_company_invoice;

    if (!result) {
      throw {
        message: 'No data returned from search_company_invoice',
        code: 'PARSE_ERROR',
      };
    }

    if (result.error?.length) {
      throw {
        message: result.error[0]?.description ?? 'API error',
        code: 'API_ERROR',
      };
    }

    if (!Array.isArray(result.company_invoice_listing)) {
      throw {
        message: 'No data returned from search_company_invoice',
        code: 'PARSE_ERROR',
      };
    }

    return toInvoicePage(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

// ─── Export Pure Transformers ─────────────────────────────────────────────────

export { toCompanyInvoice, toInvoicePage };

// ─── Export Service ───────────────────────────────────────────────────────────

export const searchCompanyInvoiceService = {
  searchCompanyInvoices,
};
